import React from 'react';
import { Box, Typography, List, ListItemButton, ListItemText, Button, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StyledSelectedItem } from '../../conf/constants';
import { LocationsTabProps } from '../types/props.types';
import { COLORS_CONFIG_PAGE } from '../../conf/constants';
import { canMoveUp, canMoveDown } from '../utils/flowUtils';

const LocationsTab: React.FC<LocationsTabProps> = ({
  flowLocations,
  selectedLocation,
  setSelectedLocation,
  locationConfig,
  setLocationConfig,
  handleRemoveLocation,
  setOpenModal,
  isMoving,
  handleMoveLocation,
}) => {
  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box
        sx={{
          width: '30%',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Process Flow Locations
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List dense>
            {flowLocations.map((location, index) => (
              <React.Fragment key={location.orderIndex}>
                {selectedLocation === index ? ( 
                  <StyledSelectedItem
                    onClick={() => setSelectedLocation(index)} 
                    sx={{ p: 2 }}
                  >
                    <ListItemText primary={`[${index}] ${location.name}`} />
                  </StyledSelectedItem>
                ) : (
                  <ListItemButton
                    onClick={() => setSelectedLocation(index)} 
                    sx={{ padding: 2 }}
                  >
                    <ListItemText primary={`[${index}] ${location.name}`} />
                  </ListItemButton>
                )}
              </React.Fragment>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            sx={{ mr: 1, bgcolor: COLORS_CONFIG_PAGE.primary, '&:hover': { bgcolor: COLORS_CONFIG_PAGE.primary } }}
          >
            NEW
          </Button>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleRemoveLocation}
            disabled={selectedLocation === null}
            sx={{ bgcolor: COLORS_CONFIG_PAGE.primary, '&:hover': { bgcolor: COLORS_CONFIG_PAGE.primary } }}
          >
            DELETE
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: '60px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          mt: 5,
        }}
      >
        <IconButton
          size="small"
          disabled={selectedLocation === null || !canMoveUp(selectedLocation) || isMoving}
          sx={{
            bgcolor: selectedLocation !== null && canMoveUp(selectedLocation) && !isMoving 
              ? COLORS_CONFIG_PAGE.primary 
              : 'grey.400',
            color: 'white',
            width: 45,
            height: 45,
            mb: 1,
            borderRadius: 1,
            '&:hover': { 
              bgcolor: selectedLocation !== null && canMoveUp(selectedLocation) && !isMoving 
                ? COLORS_CONFIG_PAGE.primary 
                : 'grey.400' 
            },
            '&:disabled': {
              bgcolor: 'grey.400',
              color: 'grey.600'
            }
          }}
          onClick={() => handleMoveLocation('up')}
        >
          <KeyboardArrowUpIcon fontSize="medium" />
        </IconButton>
        <IconButton
          size="small"
          disabled={selectedLocation === null || !canMoveDown(selectedLocation, flowLocations.length) || isMoving}
          sx={{
            bgcolor: selectedLocation !== null && canMoveDown(selectedLocation, flowLocations.length) && !isMoving 
              ? COLORS_CONFIG_PAGE.primary 
              : 'grey.400',
            color: 'white',
            width: 45,
            height: 45,
            borderRadius: 1,
            '&:hover': { 
              bgcolor: selectedLocation !== null && canMoveDown(selectedLocation, flowLocations.length) && !isMoving 
                ? COLORS_CONFIG_PAGE.primary 
                : 'grey.400' 
            },
            '&:disabled': {
              bgcolor: 'grey.400',
              color: 'grey.600'
            }
          }}
          onClick={() => handleMoveLocation('down')}
        >
          <KeyboardArrowDownIcon fontSize="medium" />
        </IconButton>
        {isMoving && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Moving...
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          p: 3,
          overflow: 'auto',
          borderLeft: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" gutterBottom>Process Flow Location</Typography>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Name</Typography>
          <TextField
            value={locationConfig.name}
            label="Name"
            onChange={(e) =>
              setLocationConfig({ ...locationConfig, name: e.target.value })
            }
            fullWidth
            size="small"
          />
        </Box>
        <Box sx={{ mt: 'auto', pt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            sx={{ bgcolor: COLORS_CONFIG_PAGE.primary }}
          >
            SAVE
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LocationsTab;